import { useCallback, useEffect, useState } from "react"
import { BorderBeam } from "./BorderBeam.tsx"
import { KnowledgeLogo } from "./KnowledgeLogo.tsx"

export type AuthContext = {
	readonly baseUrl: string
	readonly user: { readonly displayName: string; readonly email?: string; readonly username?: string }
	readonly logout: () => Promise<void>
	/**
	 * admin API 호출 중 401 을 받은 클라이언트가 호출한다. 호출 즉시 세션을 무효화해
	 * `useAdminAuth` 가 `LoginView` 를 다시 렌더하도록 만든다 (= 로그인 화면으로 복귀).
	 */
	readonly invalidateSession: () => void
}

type MeResponse = {
	user: { sub: string; email?: string; username?: string; orgId?: number; workspaceId?: number }
}
type LoginError = { error: { code: string; message?: string } }

/**
 * 어드민 인증 훅 — Black OAuth 쿠키(`knowledge_access` / `knowledge_refresh`) 기반.
 *
 * 부팅 시 `GET /admin/auth/me` 로 세션을 확인하고, 401 이면 LoginView 를 렌더한다.
 * `credentials: "include"` 로 모든 admin 요청에 쿠키가 자동 첨부된다 (동일 오리진).
 */
export function useAdminAuth(): {
	readonly context?: AuthContext
	readonly login: React.ReactElement
	readonly checking: boolean
} {
	const baseUrl = window.location.origin
	const [user, setUser] = useState<AuthContext["user"] | undefined>()
	const [checking, setChecking] = useState(true)

	const refreshSession = useCallback(async () => {
		setChecking(true)
		try {
			const res = await fetch(`${baseUrl}/admin/auth/me`, { credentials: "include" })
			if (res.status === 200) {
				const body = (await res.json()) as MeResponse
				const displayName = body.user.username ?? body.user.email ?? body.user.sub
				setUser({
					displayName,
					...(body.user.email ? { email: body.user.email } : {}),
					...(body.user.username ? { username: body.user.username } : {})
				})
			} else {
				setUser(undefined)
			}
		} catch {
			setUser(undefined)
		} finally {
			setChecking(false)
		}
	}, [baseUrl])

	useEffect(() => {
		refreshSession()
	}, [refreshSession])

	const logout = useCallback(async () => {
		await fetch(`${baseUrl}/admin/auth/logout`, { method: "POST", credentials: "include" }).catch(
			() => undefined
		)
		setUser(undefined)
	}, [baseUrl])

	/**
	 * 401 감지 시 호출되는 세션 무효화 함수. 서버 쿠키 폐기는 서버 측 책임이고,
	 * 여기서는 클라이언트 상태만 비워 `LoginView` 가 렌더되도록 전환한다.
	 */
	const invalidateSession = useCallback(() => {
		setUser(undefined)
	}, [])

	const context: AuthContext | undefined = user ? { baseUrl, user, logout, invalidateSession } : undefined

	return {
		...(context !== undefined ? { context } : {}),
		login: <AdminLogin baseUrl={baseUrl} onSuccess={refreshSession} />,
		checking
	}
}

function AdminLogin(props: { readonly baseUrl: string; readonly onSuccess: () => void }) {
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | undefined>()

	async function submit(e: React.FormEvent) {
		e.preventDefault()
		if (!email || !password) return
		setSubmitting(true)
		setError(undefined)
		try {
			const res = await fetch(`${props.baseUrl}/admin/auth/login`, {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ email, password })
			})
			if (res.status === 200) {
				props.onSuccess()
				return
			}
			const body = (await res.json().catch(() => ({}))) as Partial<LoginError>
			setError(
				res.status === 401
					? "이메일 또는 비밀번호가 올바르지 않습니다."
					: (body.error?.message ?? `로그인 실패 (${res.status})`)
			)
		} catch (err) {
			setError(`네트워크 오류: ${(err as Error).message}`)
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<div className="auth-wrap">
			<form className="auth-card" onSubmit={submit}>
				<BorderBeam size={240} duration={10} />

				<div className="auth-brand">
					<KnowledgeLogo height={32} />
					<span className="s">멀티테넌트 Knowledge PaaS</span>
				</div>

				<h1>Knowledge 콘솔 접속</h1>

				<div className="field">
					<label className="field-label" htmlFor="email">
						이메일
					</label>
					<input
						id="email"
						className="input"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="you@company.com"
						autoComplete="username"
						required
					/>
				</div>

				<div className="field">
					<label className="field-label" htmlFor="password">
						비밀번호
					</label>
					<input
						id="password"
						className="input"
						type="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						placeholder="••••••••"
						autoComplete="current-password"
						required
					/>
				</div>

				{error ? (
					<div className="field-hint" style={{ color: "var(--pk-danger)", marginTop: 8 }}>
						{error}
					</div>
				) : null}

				<button
					type="submit"
					className="btn btn-primary btn-block"
					disabled={submitting || !email || !password}
					style={{ marginTop: 20 }}
				>
					{submitting ? "인증 중..." : "접속"}
				</button>
			</form>
		</div>
	)
}
